/**
 * Role-based route guard.
 *
 * Wrap a protected route element. If the current user's role isn't in the
 * `allow` list, renders <Navigate> to /403. Renders nothing while the auth
 * context is still loading so we don't flash 403 during the initial restore.
 *
 * Server-side enforcement is the source of truth — every gated endpoint
 * re-checks the caller's role against the `compliance.users` table (per
 * the platform docs; roles are not derived from Cognito groups). This
 * guard exists to give the UI a fast, deterministic answer ("don't render
 * the page at all") rather than rendering then crashing on a 403 response.
 *
 * Usage:
 *   { path: "admin", element: (
 *       <RequireRole allow={["admin"]}>
 *         <AdminPage />
 *       </RequireRole>
 *     ) }
 */

import { Center, Loader, Stack, Text } from "@mantine/core";
import { Navigate } from "react-router-dom";
import type { UserRole } from "../api/types";
import { useAuth } from "./useAuth";

export function RequireRole({
  allow,
  children,
}: {
  allow: UserRole[];
  children: React.ReactNode;
}) {
  const { user, session, loading } = useAuth();

  if (loading) {
    return (
      <Center h="50vh">
        <Loader size="md" />
      </Center>
    );
  }

  // Session exists but canonical user hasn't loaded yet. Hold rendering —
  // never decide on a role we can't confirm from the BE.
  if (session && !user) {
    return (
      <Center h="50vh">
        <Stack align="center" gap="sm">
          <Loader size="md" />
          <Text size="sm" c="dimmed">
            Verifying your role…
          </Text>
        </Stack>
      </Center>
    );
  }

  if (!user) {
    // RequireAuth normally handles this case, but if RequireRole is used
    // without a parent RequireAuth, send them to /login rather than /403.
    return <Navigate to="/login" replace />;
  }

  if (!allow.includes(user.role)) {
    return <Navigate to="/403" replace />;
  }

  return <>{children}</>;
}
